(() => {
  if (window.OlvaxinProductDetails?.lifecycleVersion) {
    window.OlvaxinProductDetails.init(document);
    return;
  }

  const controllers = new WeakMap();
  const subscriptionContexts = new WeakMap();
  const sectionSelector = ".shop-product-section, .featured-product-section";
  const productRootSelector = ".shop-product-section, .featured-product-section, [data-section-id]";

  function sectionsIn(root) {
    if (root.matches?.(sectionSelector)) return [root];
    const sections = Array.from(root.querySelectorAll?.(sectionSelector) || []);
    if (sections.length) return sections;
    if (root.matches?.(".shop-product-layout")) return [root];
    return Array.from(root.querySelectorAll?.(".shop-product-layout") || []);
  }

  function productRoot(element) {
    return element.closest(productRootSelector) || element;
  }

  function readConfig(root, selector) {
    const node = root.querySelector(selector);
    if (!node) return {};
    try {
      return JSON.parse(node.textContent || "{}");
    } catch {
      return {};
    }
  }

  function formatCents(value) {
    const cents = Number.isFinite(Number(value)) ? Math.round(Number(value)) : 0;
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents, window.Shopify.money_format);
    return (cents / 100).toFixed(2);
  }

  function formatCentsWithCurrency(value) {
    const cents = Number.isFinite(Number(value)) ? Math.round(Number(value)) : 0;
    if (window.Shopify?.formatMoneyWithCurrency) return window.Shopify.formatMoneyWithCurrency(cents, window.Shopify.money_format);
    return formatCents(cents);
  }

  function scopedForm(scope) {
    return (
      (typeof OLVAXIN_GetProductForm === "function" && OLVAXIN_GetProductForm(scope)) ||
      scope.querySelector('form[action*="/cart/add"]') ||
      null
    );
  }

  function setHiddenInput(form, name, value, marker) {
    if (!form) return null;
    let input = form.querySelector(`input[name="${name}"][${marker}]`);
    if (!input) {
      input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.setAttribute(marker, "true");
      form.appendChild(input);
    }
    input.value = String(value);
    return input;
  }

  function setScopedVariant(scope, variantId) {
    if (variantId == null || variantId === "") return;
    if (typeof OLVAXIN_SetProductFormVariantId === "function") {
      OLVAXIN_SetProductFormVariantId(variantId, scope);
      return;
    }
    const form = scopedForm(scope);
    if (!form) return;
    let input = form.querySelector('input[name="id"], select[name="id"]');
    if (!input) input = setHiddenInput(form, "id", variantId, "data-olvaxin-quantity-break-id");
    if (input) {
      input.value = String(variantId);
      input.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  function updateProductPrices(scope, priceCents, compareCents) {
    if (priceCents != null) {
      const formattedPrice = formatCents(priceCents);
      scope.querySelectorAll(".shop-product-price, .shop-product-price-block, [data-product-price]").forEach((element) => {
        element.textContent = formattedPrice;
      });
    }
    if (compareCents != null) {
      const formattedCompare = formatCents(compareCents);
      scope.querySelectorAll(".shop-compare-price, [data-compare-price]").forEach((element) => {
        element.textContent = formattedCompare;
        element.hidden = Number(compareCents) <= Number(priceCents || 0);
      });
    }
  }

  function selectedControls(root) {
    const values = new Map();
    root.querySelectorAll("[data-option-selector]").forEach((control) => {
      const position = Number(control.dataset.optionSelector);
      if (!position) return;
      const selected = control.matches("select") || control.checked;
      if (selected && control.value) values.set(position - 1, control.dataset.optionValue || control.value);
    });
    return values;
  }

  function matchingVariant(config, controls) {
    if (!controls.size) return null;
    return (config.variants || []).find((variant) =>
      Array.from(controls).every(([index, value]) => String(variant.options?.[index] || "") === String(value)),
    );
  }

  function subscriptionPriceCents(variant, context) {
    const allocation = variant?.selling_plan_allocations?.find((item) => String(item.selling_plan_id) === String(context?.sellingPlanId));
    if (allocation?.price != null) return Math.round(Number(allocation.price));
    if (!context?.discountType) return Math.round(Number(variant?.price || 0));
    return planPriceCents(variant, { priceAdjustments: [{ valueType: context.discountType, value: context.discountValue }] });
  }

  function initQuantityBreaks(section, signal) {
    section.querySelectorAll(".quantity-break-container[data-quantity-break-runtime]").forEach((root) => {
      if (root.dataset.runtimeBound === "true") return;
      root.dataset.runtimeBound = "true";
      const config = readConfig(root, "[data-quantity-break-config]");
      const scope = productRoot(root);
      const form = scopedForm(scope);
      const activeInput = () => root.querySelector(".quantity-break-input:checked");
      const rowsFor = (container) =>
        Array.from(container?.querySelectorAll(".quantity-break-variant-selectors") || []).map((row, index) => {
          row.dataset.quantityOption = container.dataset.optionQuantity || "";
          row.dataset.itemIndex = String(index + 1);
          return row;
        });
      const rowVariant = (row) => matchingVariant(config, selectedControls(row)) || config.variants?.[0] || null;
      const packageState = (input) => {
        const container = input.closest(".quantity-break-option-container");
        const quantity = Math.max(1, Number(container?.dataset.optionQuantity || input.value || 1));
        const bundleVariantId = input.dataset.bundleVariantId;
        const bundleVariant = (config.variants || []).find((variant) => String(variant.id) === String(bundleVariantId));
        const rows = rowsFor(container);
        let variants = bundleVariantId ? [bundleVariant || { id: bundleVariantId }] : rows.map(rowVariant).filter(Boolean);
        if (!variants.length) variants = [matchingVariant(config, selectedControls(container)) || config.variants?.[0]].filter(Boolean);
        while (!bundleVariantId && variants.length && variants.length < quantity) variants.push(variants[variants.length - 1]);
        return { container, quantity, bundleVariantId, rows, variants };
      };
      const packagePrices = (input, state) => {
        const subscription = subscriptionContexts.get(scope);
        const total = state.variants.reduce((sum, variant) => sum + subscriptionPriceCents(variant, subscription), 0);
        const basePrice = total || Math.round(Number(input.dataset.price || input.dataset.basePrice || 0));
        let compareCents =
          state.variants.reduce((sum, variant) => sum + Math.round(Number(variant.compare_at_price || 0)), 0) ||
          Math.round(Number(state.container?.dataset.compareAtPrice || 0));
        let priceCents = basePrice;
        if (input.dataset.enableCustomPricing === "true") {
          const unitPrice = subscriptionPriceCents(state.variants[0], subscription) || Math.round(Number(input.dataset.basePrice || basePrice));
          const format = input.dataset.customPriceFormat || "none";
          if (format === "percentage") priceCents = Math.round(basePrice * (1 - Number(input.dataset.customPricePercentage || 0) / 100));
          if (format === "amount") priceCents = Math.round(Number(input.dataset.customPriceAmount || 0) * 100 * state.quantity);
          if (format === "bogo") priceCents = Math.round(unitPrice * Number(input.dataset.bogoMultiplier || state.quantity));
        }
        const customCompare = Number(input.dataset.customCompareAtPrice || 0);
        if (customCompare > 0) compareCents = Math.round(customCompare * 100 * state.quantity);
        input.dataset.price = String(Math.max(0, priceCents));
        return { priceCents: Math.max(0, priceCents), compareCents: Math.max(0, compareCents) };
      };
      const cartItems = (input, state, priceCents) => {
        const sellingPlanId = subscriptionContexts.has(scope)
          ? subscriptionContexts.get(scope)?.sellingPlanId || ""
          : form?.querySelector('input[name="selling_plan"]')?.value || "";
        const grouped = new Map();
        state.variants.forEach((variant) => {
          if (!variant?.id) return;
          const key = String(variant.id);
          grouped.set(key, { id: variant.id, quantity: (grouped.get(key)?.quantity || 0) + 1 });
        });
        const items = Array.from(grouped.values());
        if (sellingPlanId) items.forEach((item) => (item.selling_plan = String(sellingPlanId)));
        if (items[0] && input.dataset.enableCustomPricing === "true") {
          items[0].properties = {
            _custom_total_price: (priceCents / 100).toFixed(2),
            _quantity_option: String(state.quantity),
          };
        }
        return items;
      };
      const appendField = (container, name, value) => {
        const field = document.createElement("input");
        field.type = "hidden";
        field.name = name;
        field.value = String(value);
        container.appendChild(field);
      };
      const switchToItemsMode = (items) => {
        if (!form || !items.length) return;
        form.dataset.olvaxinQuantityBreakItemsMode = "true";
        form.querySelector('input[name="quantity"][data-olvaxin-quantity-break]')?.remove();
        const startIndex = form.querySelectorAll(".product-variant-premium-kit").length + 1;
        if (typeof OLVAXIN_ProductFormSwitchToItemsMode === "function" && OLVAXIN_ProductFormSwitchToItemsMode(items, { containerClass: "quantity-break-variant-form", startIndex }, scope)) return;
        form.querySelector(".quantity-break-variant-form")?.remove();
        const container = document.createElement("div");
        container.className = "quantity-break-variant-form";
        items.forEach((item, offset) => {
          const index = startIndex + offset;
          appendField(container, `items[${index}][id]`, item.id);
          appendField(container, `items[${index}][quantity]`, item.quantity);
          if (item.selling_plan) appendField(container, `items[${index}][selling_plan]`, item.selling_plan);
          Object.entries(item.properties || {}).forEach(([key, value]) => appendField(container, `items[${index}][properties][${key}]`, value));
        });
        form.querySelector('input[name="id"]')?.remove();
        form.appendChild(container);
        OLVAXIN_SetProductFormSellingPlan('', form);
      };
      const switchToSingleMode = (item) => {
        if (!form || !item) return;
        form.dataset.olvaxinQuantityBreakItemsMode = "false";
        form.querySelector(".quantity-break-variant-form")?.remove();
        setScopedVariant(scope, item.id);
        setHiddenInput(form, "quantity", item.quantity, "data-olvaxin-quantity-break");
        OLVAXIN_SetProductFormSellingPlan(item.selling_plan, form);
      };
      const syncCart = (input, state, priceCents) => {
        if (!form) return [];
        const items = cartItems(input, state, priceCents);
        if (!items.length) return items;
        const singleStandardItem = items.length === 1 && !items[0].properties;
        form.querySelector(".shopify-payment-button")?.toggleAttribute("hidden", !singleStandardItem);
        if (singleStandardItem) {
          switchToSingleMode(items[0]);
        } else {
          switchToItemsMode(items);
        }
        return items;
      };
      const updateRow = (row, variant) => {
        if (!variant) return;
        const image = row.querySelector(".quantity-break-variant-image img");
        const source = variant.featured_image?.src || variant.featured_media?.preview_image?.src;
        if (image && source) {
          image.src = source;
          image.alt = variant.title || "";
        }
        const name = row.querySelector(".selected-variant-name");
        if (name) name.textContent = variant.options?.filter(Boolean).length ? `(${variant.options.filter(Boolean).join(" / ")})` : "";
      };
      const updateOptionDisplay = (input, state, prices) => {
        const { container, priceCents, compareCents } = { container: state.container, ...prices };
        const hasCompare = compareCents > priceCents;
        container?.querySelectorAll(".quantity-break-price, .quantity-break-total-price, [data-bundle-price]").forEach((element) => (element.textContent = formatCents(priceCents)));
        container?.querySelectorAll(".quantity-break-original-price, [data-bundle-compare]").forEach((element) => {
          element.hidden = !hasCompare;
          element.style.visibility = hasCompare ? "visible" : "hidden";
          if (hasCompare) element.textContent = formatCents(compareCents);
        });
        const perPrice = Math.max(1, Number(input.dataset.applicationCount || 1));
        container?.querySelectorAll(".quantity-break-per-price-amount").forEach((element) => (element.textContent = formatCents(Math.round(priceCents / perPrice))));
        container?.querySelectorAll(".quantity-break-save-badge").forEach((element) => {
          const savings = Math.max(0, compareCents - priceCents);
          element.style.display = savings ? "" : "none";
          if (savings) element.textContent = `${container.dataset.saveBadgeText || (window.olvaxinStrings?.save || "Save")} ${container.dataset.saveBadgeFormat === "percentage" ? `${Math.round((savings * 100) / compareCents)}%` : formatCents(savings)}`;
        });
      };
      const updateSelectedDisplay = (input, state, prices) => {
        const { priceCents, compareCents } = prices;
        const hasCompare = compareCents > priceCents;
        root.querySelectorAll(".quantity-break-summary-price").forEach((element) => (element.textContent = formatCents(priceCents)));
        root.querySelectorAll(".quantity-break-summary-compare").forEach((element) => {
          element.hidden = !hasCompare;
          if (hasCompare) element.textContent = formatCents(compareCents);
        });
        const discount = hasCompare ? Math.round(((compareCents - priceCents) * 100) / compareCents) : 0;
        root.querySelectorAll(".quantity-break-summary-discount").forEach((element) => {
          element.hidden = !discount;
          element.textContent = discount ? `${discount}% ${(window.olvaxinStrings?.off || "OFF")}` : "";
        });
        if (config.updatePrice) updateProductPrices(scope, priceCents, compareCents);
        const label = scope.querySelector(".product-form__submit .button__text, .shop-add-to-cart-button .button-text, [data-add-to-cart-text]");
        const button = label?.closest("button, .shop-add-to-cart-button");
        if (label && button?.dataset.showPrice === "true") {
          const text = button.dataset.persistentText || button.dataset.originalText || label.dataset.runtimeText || label.textContent.trim() || (window.olvaxinStrings?.addToCart || "Add to Cart");
          label.dataset.runtimeText = text;
          label.textContent = `${text} - ${formatCentsWithCurrency(priceCents)}`;
        }
      };
      const updateAllOptionDisplays = () => {
        root.querySelectorAll(".quantity-break-input").forEach((option) => {
          const optionState = packageState(option);
          updateOptionDisplay(option, optionState, packagePrices(option, optionState));
        });
      };
      const syncPackage = (input, { refreshAll = false } = {}) => {
        if (!input?.checked) return;
        const state = packageState(input);
        state.rows.forEach((row) => updateRow(row, rowVariant(row)));
        const prices = packagePrices(input, state);
        const items = syncCart(input, state, prices.priceCents);
        if (refreshAll) updateAllOptionDisplays();
        else updateOptionDisplay(input, state, prices);
        updateSelectedDisplay(input, state, prices);
        document.dispatchEvent(
          new CustomEvent("quantity_break:changed", {
            detail: { root, scope, quantity: state.quantity, priceCents: prices.priceCents, compareCents: prices.compareCents, variantId: state.bundleVariantId || items[0]?.id || null, items },
          }),
        );
      };
      const syncSelectors = (container, source) => {
        const position = source.dataset.optionSelector;
        const value = source.dataset.optionValue || source.value;
        if (!position || !value) return;
        rowsFor(container).forEach((row) => {
          row.querySelectorAll(`[data-option-selector="${position}"]`).forEach((control) => {
            if (control === source) return;
            if (control.matches("select")) {
              if (Array.from(control.options).some((option) => option.value === value)) control.value = value;
            } else {
              control.checked = String(control.dataset.optionValue || control.value) === String(value);
            }
          });
        });
      };
      const syncSelection = (input) => {
        if (!input?.checked) return;
        root.querySelectorAll(".quantity-break-option, [data-bundle-option]").forEach((option) => option.classList.toggle("selected", option.contains(input)));
        syncPackage(input);
      };

      root.addEventListener(
        "change",
        (event) => {
          const target = event.target;
          const container = target.closest(".quantity-break-option-container");
          if (target.matches(".quantity-break-input")) syncSelection(target);
          if (target.matches("[data-option-selector]") && container?.querySelector(".quantity-break-input:checked")) {
            syncSelectors(container, target);
            syncPackage(activeInput(), { refreshAll: true });
          }
        },
        { signal },
      );
      document.addEventListener("subscriptionContext:updated", (event) => {
        if (event.detail?.scope === scope) syncPackage(activeInput(), { refreshAll: true });
      }, { signal });
      syncSelection(activeInput());
    });
  }

  function planPriceCents(variant, plan) {
    const price = Math.round(Number(variant?.price || 0));
    const adjustment = plan?.priceAdjustments?.[0];
    if (!adjustment) return price;
    const value = Number(adjustment.value || 0);
    if (adjustment.valueType === "percentage") return Math.max(0, Math.round(price * (1 - value / 100)));
    if (adjustment.valueType === "fixed_amount") return Math.max(0, price - Math.round(value));
    if (adjustment.valueType === "price") return Math.max(0, Math.round(value));
    return price;
  }

  function initSubscribeSave(section, signal) {
    const roots = Array.from(section.querySelectorAll(".subscribe-save-wrapper[data-subscribe-runtime], .subscribe-save-dynamic-outer[data-subscribe-runtime]"));
    roots.forEach((root) => {
      if (root.dataset.runtimeBound === "true") return;
      root.dataset.runtimeBound = "true";
      const config = readConfig(root, "[data-subscribe-save-config]");
      const scope = productRoot(root);
      const planNode =
        root.querySelector("[data-selling-plans]") ||
        Array.from(section.querySelectorAll("[data-selling-plans-for]")).find(
          (node) => node.dataset.sellingPlansFor === root.dataset.blockId,
        );
      let plans = [];
      if (planNode) {
        try {
          plans = JSON.parse(planNode.textContent || "[]");
        } catch {
          plans = [];
        }
      }
      let selectedQuantityVariant = null;
      const currentVariant = () => {
        const id = scopedForm(scope)?.querySelector('input[name="id"], select[name="id"]')?.value;
        return (config.variants || []).find((variant) => String(variant.id) === String(id)) || selectedQuantityVariant || config.variants?.[0] || null;
      };
      const planFor = (id) => (plans.length ? plans : config.plans || []).find((plan) => String(plan.id) === String(id)) || config.plan || null;
      const renderPrices = (plan, prices = {}) => {
        const variant = prices.variant || currentVariant();
        if (!variant) return;
        const subscriptionPrice = prices.subscriptionPriceCents ?? planPriceCents(variant, plan);
        const subscriptionCompare = prices.subscriptionCompareCents ?? Math.round(Number(variant.compare_at_price || 0));
        const oneTimePrice = prices.oneTimePriceCents ?? Math.round(Number(variant.price || 0));
        const oneTimeCompare = prices.oneTimeCompareCents ?? Math.round(Number(variant.compare_at_price || 0));
        root.querySelectorAll(".subscribe-price-current").forEach((element) => (element.textContent = formatCents(subscriptionPrice)));
        root.querySelectorAll(".subscribe-price-compare").forEach((element) => {
          element.hidden = subscriptionCompare <= subscriptionPrice;
          if (subscriptionCompare > subscriptionPrice) element.textContent = formatCents(subscriptionCompare);
        });
        root.querySelectorAll(".subscribe-one-time-price").forEach((element) => (element.textContent = formatCents(oneTimePrice)));
        root.querySelectorAll(".subscribe-one-time-price-compare").forEach((element) => {
          element.hidden = oneTimeCompare <= oneTimePrice;
          if (oneTimeCompare > oneTimePrice) element.textContent = formatCents(oneTimeCompare);
        });
      };
      const applyPlan = (planId) => {
        const form = scopedForm(scope);
        if (!form) return;
        OLVAXIN_SetProductFormSellingPlan(planId, form);
        const plan = planFor(planId);
        if (!planId) {
          const quantityInput = form?.querySelector('input[name="quantity"][data-olvaxin-subscribe-quantity]');
          if (quantityInput) {
            quantityInput.value = "1";
            quantityInput.dispatchEvent(new Event("change", { bubbles: true }));
          }
          subscriptionContexts.set(scope, null);
          document.dispatchEvent(new CustomEvent("subscriptionContext:updated", { detail: { scope, subscriptionContext: null } }));
          return;
        }
        if (config.mode === "custom" && config.quantity != null && !form.querySelector('[data-olvaxin-quantity-break]')) {
          const quantityInput = setHiddenInput(form, "quantity", Math.max(1, Number(config.quantity) || 1), "data-olvaxin-subscribe-quantity");
          quantityInput?.dispatchEvent(new Event("change", { bubbles: true }));
        }
        const variant = currentVariant();
        const priceWithDiscount = planPriceCents(variant, plan);
        const adjustment = plan?.priceAdjustments?.[0];
        const subscriptionContext = {
          originalPrice: Math.round(Number(variant?.price || 0)),
          priceWithDiscount,
          finalPrice: priceWithDiscount,
          sellingPlanId: String(planId),
          discountType: adjustment?.valueType || null,
          discountValue: Number(adjustment?.value || 0),
        };
        subscriptionContexts.set(scope, subscriptionContext);
        renderPrices(plan);
        document.dispatchEvent(new CustomEvent("subscriptionContext:updated", { detail: { scope, subscriptionContext } }));
      };
      const checkedToggle = () => root.querySelector(".subscribe-toggle:checked, .subscribe-toggle-dynamic:checked");
      const selectedPlan = (toggle) => {
        if (config.mode === "custom") return config.planId || root.dataset.sellingPlanId || "";
        const wrapper = toggle?.closest(".subscribe-save-wrapper, .subscribe-save-wrapper-dynamic") || root;
        return wrapper.querySelector(".subscribe-frequency-dropdown")?.value || config.planId || root.dataset.sellingPlanId || "";
      };

      root.addEventListener(
        "change",
        (event) => {
          const target = event.target;
          if (target.matches(".subscribe-toggle, .subscribe-toggle-dynamic")) {
            if (target.checked) {
              root.querySelectorAll(".subscribe-toggle, .subscribe-toggle-dynamic").forEach((toggle) => {
                if (toggle !== target) toggle.checked = false;
              });
              root.querySelectorAll(".subscribe-one-time-choice").forEach((choice) => (choice.checked = false));
              applyPlan(selectedPlan(target));
            } else if (!checkedToggle()) {
              root.querySelectorAll(".subscribe-one-time-choice").forEach((choice) => (choice.checked = true));
              applyPlan("");
            }
          } else if (target.matches(".subscribe-one-time-choice") && target.checked) {
            root.querySelectorAll(".subscribe-toggle, .subscribe-toggle-dynamic").forEach((toggle) => (toggle.checked = false));
            applyPlan("");
          } else if (config.mode === "dynamic" && target.matches(".subscribe-frequency-dropdown") && checkedToggle()) {
            applyPlan(target.value);
          }
        },
        { signal },
      );
      root.querySelectorAll(".subscribe-save-container").forEach((container) => {
        container.addEventListener(
          "click",
          (event) => {
            if (event.target.closest("a, button, select, option, input, label, .subscribe-toggle-switch")) return;
            const toggle = container.querySelector(".subscribe-toggle, .subscribe-toggle-dynamic");
            if (!toggle) return;
            toggle.checked = !toggle.checked;
            toggle.dispatchEvent(new Event("change", { bubbles: true }));
          },
          { signal },
        );
      });
      document.addEventListener(
        "variant:changed",
        () => renderPrices(planFor(selectedPlan(checkedToggle()))),
        { signal },
      );
      document.addEventListener(
        "variant:change",
        () => renderPrices(planFor(selectedPlan(checkedToggle()))),
        { signal },
      );
      window.addEventListener(
        "variantChanged",
        () => renderPrices(planFor(selectedPlan(checkedToggle()))),
        { signal },
      );
      document.addEventListener(
        "quantity_break:changed",
        (event) => {
          if (event.detail?.scope !== scope) return;
          selectedQuantityVariant = (config.variants || []).find((variant) => String(variant.id) === String(event.detail.variantId)) || null;
          const isSubscribed = Boolean(checkedToggle());
          renderPrices(planFor(selectedPlan(checkedToggle())), {
            variant: selectedQuantityVariant,
            ...(isSubscribed
              ? { subscriptionPriceCents: event.detail.priceCents, subscriptionCompareCents: event.detail.compareCents }
              : { oneTimePriceCents: event.detail.priceCents, oneTimeCompareCents: event.detail.compareCents }),
          });
        },
        { signal },
      );
      const activeToggle = checkedToggle();
      if (activeToggle) applyPlan(selectedPlan(activeToggle));
      else {
        applyPlan("");
        renderPrices(planFor(config.planId));
      }
    });
  }

  function init(section) {
    if (controllers.has(section)) return;
    const controller = new AbortController();
    const { signal } = controller;
    controllers.set(section, controller);

    section.querySelectorAll('form[data-type="add-to-cart-form"]').forEach((form) => {
      const formId = form.id;
      const quantityInputs = section.querySelectorAll(
        `input[name="quantity"][form="${formId}"], .modern-quantity-selector .quantity-input[form="${formId}"]`,
      );
      if (!quantityInputs.length) return;
      form.addEventListener(
        "submit",
        () => {
          const managedQuantity = form.querySelector(
            'input[name="quantity"][data-olvaxin-quantity-break], input[name="quantity"][data-olvaxin-subscribe-quantity]',
          );
          if (managedQuantity) return;
          const quantity = Number.parseInt(quantityInputs[0].value, 10) || 1;
          form.querySelectorAll('input[type="hidden"][name="quantity"]').forEach((input) => input.remove());
          const quantityInput = document.createElement("input");
          quantityInput.type = "hidden";
          quantityInput.name = "quantity";
          quantityInput.value = String(quantity);
          form.appendChild(quantityInput);
        },
        { signal },
      );
    });

    const titles = section.querySelectorAll(".shop-product-title[data-font-size][data-mobile-font-size]");
    const updateTitles = () => {
      const attribute = window.innerWidth <= 767 ? "data-mobile-font-size" : "data-font-size";
      titles.forEach((title) => title.style.setProperty("font-size", `${title.getAttribute(attribute)}px`, "important"));
    };
    updateTitles();
    window.addEventListener("resize", updateTitles, { signal, passive: true });

    section.querySelectorAll(".product-faq").forEach((faq) => {
      const items = faq.querySelectorAll(".faq-item");
      const accordion = faq.querySelector(".faq-container")?.dataset.accordion === "true";
      items.forEach((item) => {
        item.querySelector(".faq-question")?.addEventListener(
          "click",
          () => {
            const active = item.classList.contains("active");
            if (accordion && !active) items.forEach((other) => other.classList.remove("active"));
            item.classList.toggle("active");
          },
          { signal },
        );
      });
    });

    section.querySelectorAll(".shop-read-more, .shop-read-less").forEach((link) => {
      link.addEventListener(
        "click",
        (event) => {
          event.preventDefault();
          const description = link.closest(".shop-product-description");
          if (!description) return;
          const showFull = link.classList.contains("shop-read-more");
          const truncated = description.querySelector(".shop-truncated-description");
          const full = description.querySelector(".shop-full-description");
          if (truncated) truncated.style.display = showFull ? "none" : "block";
          if (full) full.style.display = showFull ? "block" : "none";
        },
        { signal },
      );
    });

    initSubscribeSave(section, signal);
    initQuantityBreaks(section, signal);
    section.querySelectorAll('[data-product-purchase-pending]').forEach((info) => {
      info.inert = false;
      info.removeAttribute('aria-busy');
      info.removeAttribute('data-product-purchase-pending');
    });
  }

  function initAll(root = document) {
    sectionsIn(root).forEach(init);
  }

  function disposeAll(root) {
    sectionsIn(root).forEach((section) => {
      controllers.get(section)?.abort();
      controllers.delete(section);
      subscriptionContexts.delete(section);
      section.querySelectorAll("[data-runtime-bound]").forEach((element) => delete element.dataset.runtimeBound);
    });
  }

  if (document.readyState === 'loading') document.addEventListener("DOMContentLoaded", () => initAll(), { once: true });
  else initAll();
  document.addEventListener("shopify:section:load", (event) => initAll(event.target));
  document.addEventListener("shopify:section:unload", (event) => disposeAll(event.target));
  window.OlvaxinProductDetails = { lifecycleVersion: 2, init: initAll, dispose: disposeAll };
})();
